import { useState } from 'react'
import { format } from 'date-fns'

import { CalendarGridMode } from '../../../../constants/calendarGrid'
import { DATE_API_DATA_FORMAT_SHORT } from '../../../../constants/date'
import { useDate, useDateLocale, useQueryParamSubTab } from '../../../../hooks'
import { CALENDAR_GRID_MODES_DEFAULT } from '../../CalendarGrid.constants'
import {
  getIsCalendarGridAgendaMode,
  getIsCalendarGridMonthMode,
} from '../../CalendarGrid.utils'
import { useCalendarGridItems } from '../useCalendarGridItems'

import {
  CALENDAR_GRID_MODE_PARAM,
  CALENDAR_GRID_TODAY_ANCHOR_OFFSET,
} from './useCalendarGrid.constants'
import {
  getCalendarGridAnchor,
  getCalendarGridRange,
  getIsCalendarGridTodayVisible,
} from './useCalendarGrid.utils'

export function useCalendarGrid({
  getItems,
  initialMode = CalendarGridMode.week,
  isEnabled,
  modes = CALENDAR_GRID_MODES_DEFAULT,
  requestKey,
  onChangeMode,
}) {
  const [anchorOffset, setAnchorOffset] = useState(
    CALENDAR_GRID_TODAY_ANCHOR_OFFSET
  )

  const locale = useDateLocale()

  const today = useDate()

  const { activeSubTab: mode, setActiveSubTab: setMode } = useQueryParamSubTab({
    allowedValues: modes,
    defaultValue: initialMode,
    paramKey: CALENDAR_GRID_MODE_PARAM,
    writeDefaultOnEntry: false,
  })

  const anchor = getCalendarGridAnchor({ anchorOffset, mode, today })

  const { days, from, to, weekdays } = getCalendarGridRange({
    anchor,
    locale,
    mode,
  })

  const calendarItems = useCalendarGridItems({
    from,
    getItems,
    isEnabled,
    requestKey,
    to,
  })

  function handleGoToPrev() {
    setAnchorOffset(prevOffset => prevOffset - 1)
  }

  function handleGoToNext() {
    setAnchorOffset(prevOffset => prevOffset + 1)
  }

  function handleGoToToday() {
    setAnchorOffset(CALENDAR_GRID_TODAY_ANCHOR_OFFSET)
  }

  function handleChangeMode(nextMode) {
    setAnchorOffset(CALENDAR_GRID_TODAY_ANCHOR_OFFSET)

    onChangeMode?.(nextMode)

    setMode(nextMode)
  }

  return {
    ...calendarItems,
    anchor,
    days,
    handleChangeMode,
    handleGoToNext,
    handleGoToPrev,
    handleGoToToday,
    isAgendaMode: getIsCalendarGridAgendaMode(mode),
    isMonthMode: getIsCalendarGridMonthMode(mode),
    isTodayVisible: getIsCalendarGridTodayVisible({ anchor, mode, today }),
    locale,
    mode,
    modes,
    now: new Date(today).toISOString(),
    today: format(today, DATE_API_DATA_FORMAT_SHORT),
    weekdays,
  }
}
